import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { PageLoader } from '../components/LoadingSpinner';
import { CreatorBadges } from '../components/VerifiedBadge';
import { toast } from 'sonner';
import { 
  MessageCircle, Heart, Send, ChevronLeft, Sparkles, HelpCircle, 
  Lightbulb, PartyPopper, MessagesSquare, Store, Clock, Trash2,
  ThumbsUp, Star, Smile
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_ICONS = {
  'show-tell': Sparkles,
  'questions': HelpCircle,
  'tips': Lightbulb,
  'celebrations': PartyPopper,
  'general': MessagesSquare
};

const CHANNEL_COLORS = {
  'show-tell': 'bg-pastel-crochet/30',
  'questions': 'bg-pastel-art/30',
  'tips': 'bg-pastel-stickers/30',
  'celebrations': 'bg-pastel-keyrings/30',
  'general': 'bg-muted'
};

// Reaction configuration (same as CommunityPage)
const REACTIONS = {
  love: { icon: Heart, label: 'Love', color: 'text-red-500', bgColor: 'bg-red-500/10' },
  helpful: { icon: ThumbsUp, label: 'Helpful', color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  inspiring: { icon: Star, label: 'Inspiring', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
  relatable: { icon: Smile, label: 'Relatable', color: 'text-purple-500', bgColor: 'bg-purple-500/10' }
};

// Reaction Button Component
const ReactionButton = ({ type, count, isActive, onClick }) => {
  const config = REACTIONS[type];
  const Icon = config.icon;
  
  return (
    <button
      onClick={() => onClick(type)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
        isActive 
          ? `${config.bgColor} ${config.color} font-medium` 
          : 'text-muted-foreground hover:bg-muted'
      }`}
      title={config.label}
      data-testid={`reaction-${type}`}
    >
      <Icon className={`w-4 h-4 ${isActive ? 'fill-current' : ''}`} />
      <span>{config.label}</span>
      {count > 0 && <span className="font-semibold">({count})</span>}
    </button>
  );
};

export default function CommunityPostPage() {
  const { postId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myReactions, setMyReactions] = useState([]);
  const [reactions, setReactions] = useState({});
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (!user?.is_creator) {
      navigate('/');
      return;
    }
    fetchPost();
  }, [postId, user]);

  useEffect(() => {
    if (token && post) {
      fetchMyReactions();
    }
  }, [postId, token, post]);

  const fetchPost = async () => {
    try {
      const res = await fetch(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPost(data);
        setReactions(data.reactions || {});
      } else {
        toast.error('Post not found');
        navigate('/community');
      }
    } catch (e) {
      console.error(e);
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReactions = async () => {
    try {
      const res = await fetch(`${API}/community/posts/${postId}/my-reactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMyReactions(await res.json());
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReaction = async (type) => {
    if (!token) {
      toast.error('Please login to react');
      return;
    }
    
    const isActive = myReactions.includes(type);
    
    try {
      if (isActive) {
        // Remove reaction
        const res = await fetch(`${API}/community/posts/${postId}/react/${type}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setMyReactions(myReactions.filter(r => r !== type));
          setReactions({ ...reactions, [type]: Math.max(0, (reactions[type] || 0) - 1) });
        }
      } else {
        // Add reaction
        const res = await fetch(`${API}/community/posts/${postId}/react`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ reaction_type: type })
        });
        if (res.ok) {
          setMyReactions([...myReactions, type]);
          setReactions({ ...reactions, [type]: (reactions[type] || 0) + 1 });
        }
      }
    } catch (e) {
      toast.error('Failed to update reaction');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please login to comment');
      return;
    }
    if (!comment.trim()) return;

    setSubmittingComment(true);
    try {
      const res = await fetch(`${API}/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: comment })
      });
      if (res.ok) {
        setComment('');
        fetchPost(); // Refresh to get new comment
        toast.success('Comment added!');
      }
    } catch (e) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API}/community/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPost({
          ...post,
          comments: post.comments.filter(c => c.id !== commentId)
        });
        toast.success('Comment deleted');
      }
    } catch (e) {
      toast.error('Failed to delete comment');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <PageLoader />;
  if (!post) return null;

  const ChannelIcon = CHANNEL_ICONS[post.channel] || MessagesSquare;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link 
          to="/community" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Community
        </Link>

        {/* Post */}
        <div className="card-brutal p-6 mb-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <Link 
              to={post.store ? `/store/${post.store.id}` : '#'}
              className="w-12 h-12 rounded-full bg-pastel-art/30 flex items-center justify-center font-bold flex-shrink-0"
            >
              {post.user?.avatar ? (
                <img src={post.user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                post.user?.name?.[0]?.toUpperCase()
              )}
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{post.user?.name}</span>
                {post.store && (
                  <CreatorBadges 
                    isVerified={post.store.is_verified}
                    isFoundingCreator={post.store.is_founding_creator}
                    tier={post.store.tier}
                    size="sm"
                  />
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${CHANNEL_COLORS[post.channel]}`}>
                  <ChannelIcon className="w-3 h-3" />
                  {post.channel.replace('-', ' ')}
                </span>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(post.created_at)}
              </p>
            </div>
          </div>

          {/* Content */}
          <h1 className="text-2xl font-heading font-bold mb-3">{post.title}</h1>
          <p className="text-muted-foreground whitespace-pre-wrap mb-4">{post.content}</p>

          {/* Images */}
          {post.images?.length > 0 && (
            <div className={`grid gap-3 mb-4 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {post.images.map((img, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden bg-muted">
                  <img src={img} alt="" className="w-full h-auto" />
                </div>
              ))}
            </div>
          )}

          {/* Linked Product */}
          {post.product && (
            <Link 
              to={`/product/${post.product.id}`}
              className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 mb-4 hover:bg-muted transition-colors"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted">
                {post.product.images?.[0] ? (
                  <img src={post.product.images[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Store className="w-8 h-8 m-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold">{post.product.name}</p>
                <p className="text-sm text-muted-foreground">${post.product.price?.toFixed(2)}</p>
              </div>
              <Button variant="outline" size="sm">View Product</Button>
            </Link>
          )}

          {/* Reactions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-primary/10">
            {Object.keys(REACTIONS).map((type) => (
              <ReactionButton
                key={type}
                type={type}
                count={reactions[type] || 0}
                isActive={myReactions.includes(type)}
                onClick={handleReaction}
              />
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 pt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              {Object.values(reactions).reduce((a, b) => a + b, 0)} reactions
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              {post.comments?.length || 0} comments
            </span>
          </div>
        </div>

        {/* Comments Section */}
        <div className="card-brutal p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Comments</h2>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleComment} className="flex gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-pastel-stickers/30 flex items-center justify-center font-bold flex-shrink-0">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 flex gap-2">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="min-h-[44px] resize-none"
                  data-testid="comment-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  disabled={submittingComment || !comment.trim()}
                  data-testid="submit-comment-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-4 mb-6 bg-muted/50 rounded-xl">
              <p className="text-muted-foreground">
                <Link to="/login" className="text-foreground font-semibold hover:underline">Login</Link> to join the conversation
              </p>
            </div>
          )}

          {/* Comments List */}
          {post.comments?.length > 0 ? (
            <div className="space-y-4">
              {post.comments.map((c) => (
                <div key={c.id} className="flex gap-3 group">
                  <Link 
                    to={c.store ? `/store/${c.store.id}` : '#'}
                    className="w-10 h-10 rounded-full bg-pastel-art/30 flex items-center justify-center font-bold flex-shrink-0"
                  >
                    {c.user?.name?.[0]?.toUpperCase()}
                  </Link>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{c.user?.name}</span>
                      {c.store?.is_verified && (
                        <CreatorBadges isVerified={true} size="sm" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatTime(c.created_at)}
                      </span>
                      {user?.id === c.user_id && (
                        <button 
                          onClick={() => handleDeleteComment(c.id)}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all ml-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              No comments yet. Be the first to share your thoughts!
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
